/**
 * 菜单权限配置
 *
 * 根据 user.type（company/unit/team/staff）和 user.is_promo / user.is_leader
 * 对侧边栏菜单做裁剪，与后端控制器权限边界一致。
 *
 * 规则说明：
 * - company（机构管理员）: 全部菜单
 * - unit（战队）/ team（小组）: 业务菜单全，但无组织管理/基础数据/续费
 * - staff（普通销售员工）: 业务菜单（仅自己数据范围），无组织/基础数据/续费/短信配置
 * - staff + is_leader（队长）: 同销售 + 短信工具可见
 * - staff + is_promo（推广员工）: 仅工作台 + 线索列表（线索录入入口在列表页内，仅推广人员可见）
 *
 * hidePromo  = 推广人员不可见
 * leaderOnly = 仅管理员/队长/unit/team 可见（含审核权限或短信工具等）
 * companyOnly = 仅机构管理员可见（组织管理/基础数据/续费）
 * salesOnly  = 仅机构管理员/销售员工可见（可创建订单的主体）
 */

// 全量导航定义（按路由 path → 侧边栏对应理想 entries 映射，含子菜单结构）
export const MENU_GROUPS = [
  {
    key: 'dashboard',
    type: 'item',
    icon: 'el-icon-data-board',
    title: '工作台',
    path: '/dashboard',
    rules: { hidePromo: false, leaderOnly: false, companyOnly: false }
  },
  {
    key: 'lead',
    type: 'item',
    icon: 'el-icon-user',
    title: '线索列表',
    path: '/lead/list',
    rules: { hidePromo: false, leaderOnly: false, companyOnly: false }
  },
  {
    key: 'order',
    type: 'submenu',
    icon: 'el-icon-tickets',
    title: '订单管理',
    rules: { hidePromo: true, leaderOnly: false, companyOnly: false },
    children: [
      { title: '工作台', path: '/order/console', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } },
      { title: '订单列表', path: '/order/list', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } },
      { title: '收款管理', path: '/bill/list', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } },
      { title: '退款管理', path: '/refund/list', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } },
      { title: '分期管理', path: '/installment/list', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } }
    ]
  },
  {
    key: 'agreement',
    type: 'submenu',
    icon: 'el-icon-document',
    title: '协议管理',
    // 协议管理：机构管理员 / 销售组长(unit/team) / 销售员工可见；推广体系隐藏
    rules: { hidePromo: true, leaderOnly: false, companyOnly: false },
    children: [
      { title: '协议列表', path: '/agreement/list', rules: { hidePromo: true, leaderOnly: false, companyOnly: false } },
      { title: '模板管理', path: '/agreement/template', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } }
    ]
  },
  {
    key: 'stats',
    type: 'item',
    icon: 'el-icon-data-analysis',
    title: '统计分析',
    path: '/stats/index',
    rules: { hidePromo: true, leaderOnly: false, companyOnly: false }
  },
  {
    key: 'analysis',
    type: 'item',
    icon: 'el-icon-pie-chart',
    title: '数据分析',
    path: '/stats/analysis',
    // 数据分析：仅机构管理员可见
    rules: { hidePromo: true, leaderOnly: false, companyOnly: true }
  },
  {
    key: 'tools',
    type: 'submenu',
    icon: 'el-icon-setting',
    title: '工具',
    // 推广权限账号不需要工具菜单
    rules: { hidePromo: true },
    children: [
      // 与后端 Sms::checkAuditor 对齐：company 或 队长 staff（排除 unit/team）
      { title: '短信群发', path: '/tools/sms', rules: { staffLeader: true, smsEnabled: true } },
      // 名额申请表 / 兼职表：仅机构管理员 / 销售员工（非推广）可见
      { title: '名额申请表', path: '/tools/quote', rules: { salesOnly: true } },
      { title: '兼职表', path: '/tools/work', rules: { salesOnly: true } }
    ]
  },
  {
    key: 'org',
    type: 'submenu',
    icon: 'el-icon-s-management',
    title: '组织架构',
    // 组织架构对机构管理员/小组组长（含推广组长）可见；
    // 组长仅能看到「员工管理」，其余子项仍仅管理员（hidePromo 刻意不放，否则推广组长会整组消失）
    rules: { hidePromo: false, leaderOnly: false, companyOnly: false, companyOrTeam: true },
    children: [
      { title: '主体信息', path: '/org/index', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } },
      { title: '战队管理', path: '/org/unit', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } },
      { title: '小组管理', path: '/org/team', rules: { hidePromo: false, leaderOnly: false, companyOnly: false, companyOrUnit: true } },
      { title: '员工管理', path: '/org/staff', rules: { hidePromo: false, leaderOnly: false, companyOnly: false, companyOrTeam: true } }
    ]
  },
  {
    key: 'base',
    type: 'submenu',
    icon: 'el-icon-menu',
    title: '基础数据',
    rules: { hidePromo: true, leaderOnly: false, companyOnly: true },
    children: [
      { title: '商品管理', path: '/base/product', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } },
      { title: '收款平台', path: '/base/paytype', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } },
      { title: '来源渠道', path: '/base/channel', rules: { hidePromo: true, leaderOnly: false, companyOnly: true } }
    ]
  },
  {
    key: 'recharge',
    type: 'item',
    icon: 'el-icon-coin',
    title: '续费充值',
    path: '/recharge/index',
    rules: { hidePromo: true, leaderOnly: false, companyOnly: true }
  }
]

/**
 * 由 user 一次性推导出各类权限布尔（供规则判定复用，避免每项菜单重复判断）
 */
function buildCtx(user, smsEnabled) {
  const isCompany = user.type === 'company'
  const isPromo = user.type === 'staff' && parseInt(user.is_promo) === 1
  return {
    smsEnabled,
    isPromoSys: !isCompany && (isPromo || parseInt(user.unit_type) === 1), // 推广体系（含推广战队/小组/员工，company 不算）
    isCompany,
    // 有审核/队长权限：管理员 或 队长 staff（ln）
    hasLeaderPriv: isCompany || (user.type === 'staff' && parseInt(user.is_leader) === 1) || user.type === 'unit' || user.type === 'team',
    // 可建单：管理员 或 销售员工（非推广）
    canCreateOrder: isCompany || (user.type === 'staff' && !isPromo),
    // company 或 队长 staff（短信群发）
    canStaffLeader: isCompany || (user.type === 'staff' && parseInt(user.is_leader) === 1),
    // 员工管理：管理员、战队长(unit) 或 小组组长（team）
    canManageStaff: isCompany || user.type === 'unit' || user.type === 'team',
    // 小组管理：管理员 或 战队长(unit)
    canManageUnit: isCompany || user.type === 'unit'
  }
}

/**
 * 判定一份 rules（顶层或子项通用）是否通过
 * @param {object} rule - 某条目的 rules
 * @param {object} ctx - buildCtx 推导出的权限布尔
 */
function passRule(rule, ctx) {
  if (rule.hidePromo && ctx.isPromoSys) return false  // 推广体系隐藏
  if (rule.companyOnly && !ctx.isCompany) return false // 仅机构管理员
  if (rule.leaderOnly && !ctx.hasLeaderPriv) return false // 仅管理员/队长/unit/team
  if (rule.salesOnly && !ctx.canCreateOrder) return false // 仅管理员/销售员工
  if (rule.companyOrTeam && !ctx.canManageStaff) return false // 仅管理员/战队长/小组组长
  if (rule.companyOrUnit && !ctx.canManageUnit) return false // 仅管理员/战队长
  return true
}

/**
 * 判定子项特有的 rules（在 passRule 之外叠加）
 */
function passChildRule(rule, ctx) {
  if (rule.staffLeader && !ctx.canStaffLeader) return false // company 或 队长 staff
  if (rule.smsEnabled && !ctx.smsEnabled) return false     // 短信模块未开启时隐藏
  return true
}

/**
 * 根据用户身份过滤菜单组
 * @param {object} user - store 中的 user 对象（含 type/is_leader/is_promo/unit_type）
 * @param {object} opts - 附加能力开关，如 { smsEnabled: boolean }
 * @returns {array} 过滤后的菜单组
 */
export function filterMenus(user, opts = {}) {
  if (!user) return []
  const { smsEnabled = true } = opts
  const ctx = buildCtx(user, smsEnabled)

  return MENU_GROUPS
    // 顶层规则过滤
    .filter(group => passRule(group.rules, ctx))
    // 子菜单逐一过滤（通用规则 + 子项专属规则）
    .map(group => {
      if (group.type !== 'submenu' || !group.children) return group
      const children = group.children.filter(child =>
        passRule(child.rules, ctx) && passChildRule(child.rules, ctx)
      )
      return { ...group, children }
    })
    // 去掉子菜单已全空的组
    .filter(group => !(group.type === 'submenu' && group.children && group.children.length === 0))
}